/**
 * TransactionReviewTable Component
 * Table for reviewing and selecting transactions before import
 * Supports pagination for large imports
 */
import { useState, useMemo } from 'react';
import TransactionRow from './TransactionRow';
import { DuplicateComparisonModal } from '@/components/documents/DuplicateModal';
import { EditTransactionModal } from '@/components/documents/EditTransactionModal';
import type { TransactionObservation } from '@/types/document';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface TransactionReviewTableProps {
  observations: TransactionObservation[];
  selectedIds: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  onSelect: (id: string, checked: boolean) => void;
  onObservationUpdated: () => void;
  stagedImportId: string;
  /** Initial page size (default: 25) */
  initialPageSize?: number;
}

export function TransactionReviewTable({
  observations,
  selectedIds,
  allSelected,
  // someSelected is passed in for parent component compatibility but we use our own visibleSomeSelected
  someSelected: _someSelected,
  onSelectAll,
  onSelect,
  onObservationUpdated,
  stagedImportId,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: TransactionReviewTableProps) {
  // Modal state
  const [editingObservation, setEditingObservation] = useState<TransactionObservation | null>(null);
  const [viewingDuplicate, setViewingDuplicate] = useState<TransactionObservation | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Calculate pagination
  const totalItems = observations.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Get paginated observations
  const paginatedObservations = useMemo(() => {
    return observations.slice(startIndex, endIndex);
  }, [observations, startIndex, endIndex]);

  // Check if all visible items are selected
  const visibleAllSelected = useMemo(() => {
    return paginatedObservations.length > 0 && paginatedObservations.every((o) => selectedIds.has(o.id));
  }, [paginatedObservations, selectedIds]);

  const visibleSomeSelected = useMemo(() => {
    const selectedOnPage = paginatedObservations.filter((o) => selectedIds.has(o.id)).length;
    return selectedOnPage > 0 && selectedOnPage < paginatedObservations.length;
  }, [paginatedObservations, selectedIds]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    // Reset to first page when changing page size
    setCurrentPage(1);
  };

  // Handle select all on current page
  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      // Select all on current page
      paginatedObservations.forEach((o) => {
        if (!selectedIds.has(o.id)) {
          onSelect(o.id, true);
        }
      });
    } else {
      // Deselect all on current page
      paginatedObservations.forEach((o) => {
        if (selectedIds.has(o.id)) {
          onSelect(o.id, false);
        }
      });
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Checkbox column */}
              <th scope="col" className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={visibleAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = visibleSomeSelected;
                  }}
                  onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  aria-label="Select all transactions on this page"
                />
              </th>

              {/* Date column */}
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>

              {/* Amount column */}
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Amount
              </th>

              {/* Description column */}
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Description
              </th>

              {/* Category column (WP-DOC-26) */}
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Category
              </th>

              {/* Status column */}
              <th
                scope="col"
                className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>

              {/* Actions column */}
              <th scope="col" className="w-20 px-4 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedObservations.map((observation) => (
              <TransactionRow
                key={observation.id}
                observation={observation}
                selected={selectedIds.has(observation.id)}
                onSelect={(checked) => onSelect(observation.id, checked)}
                onEdit={() => setEditingObservation(observation)}
                onViewDuplicate={() => setViewingDuplicate(observation)}
                stagedImportId={stagedImportId}
                onObservationUpdated={onObservationUpdated}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSelectAll={onSelectAll}
          allSelected={allSelected}
        />
      )}

      {/* Edit Modal */}
      <EditTransactionModal
        isOpen={!!editingObservation}
        observation={editingObservation}
        stagedImportId={stagedImportId}
        onClose={() => setEditingObservation(null)}
        onSaved={() => {
          setEditingObservation(null);
          onObservationUpdated();
        }}
      />

      {/* Duplicate Comparison Modal */}
      <DuplicateComparisonModal
        isOpen={!!viewingDuplicate}
        observation={viewingDuplicate}
        stagedImportId={stagedImportId}
        onClose={() => setViewingDuplicate(null)}
        onResolved={() => {
          setViewingDuplicate(null);
          onObservationUpdated();
        }}
      />
    </>
  );
}

// ============================================================================
// Pagination Component
// ============================================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSelectAll: (checked: boolean) => void;
  allSelected: boolean;
}

function Pagination({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSelectAll,
  allSelected,
}: PaginationProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 px-2">
      {/* Left side: Page info and select all */}
      <div className="flex items-center gap-4 text-sm text-gray-700">
        <span>
          Showing {startIndex + 1}-{endIndex} of {totalItems}
        </span>
        <button
          type="button"
          onClick={() => onSelectAll(!allSelected)}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {allSelected ? 'Deselect all' : 'Select all'} {totalItems}
        </button>
      </div>

      {/* Right side: Page size and navigation */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700">
            Per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
            aria-label="Items per page"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <PaginationButton
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            aria-label="First page"
          >
            <ChevronDoubleLeftIcon />
          </PaginationButton>
          <PaginationButton
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </PaginationButton>

          <span className="px-3 py-1 text-sm text-gray-700">
            {currentPage} / {totalPages}
          </span>

          <PaginationButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </PaginationButton>
          <PaginationButton
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            aria-label="Last page"
          >
            <ChevronDoubleRightIcon />
          </PaginationButton>
        </nav>
      </div>
    </div>
  );
}

interface PaginationButtonProps {
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
  'aria-label': string;
}

function PaginationButton({ onClick, disabled, children, 'aria-label': ariaLabel }: PaginationButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="p-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      {children}
    </button>
  );
}

// ============================================================================
// Icons
// ============================================================================

function ChevronLeftIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ChevronDoubleLeftIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
    </svg>
  );
}

function ChevronDoubleRightIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
    </svg>
  );
}

export default TransactionReviewTable;
